import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/slices/authentication";
import {
  useGetMyConnectProfileQuery,
  useUpsertConnectProfileMutation,
} from "../../../../store/slices/api";
import { SharedButton, SharedLoading, SharedFeatureError } from "../../../shared";
import { getItem as safeGetItem, setItem as safeSetItem } from "../../../../utils/safeStorage";

function CorrespondenceSettings({ showSocialSettings = false }) {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;

  const {
    data: profileData,
    isFetching,
    isError,
    refetch,
  } = useGetMyConnectProfileQuery(individualId, { skip: !individualId });
  // null means "no profile yet", which is a real answer; undefined means the
  // saved settings haven't arrived.
  const profileLoaded = profileData !== undefined;
  const [upsertProfile, { isLoading: isSaving }] =
    useUpsertConnectProfileMutation();

  // Server-side toggles
  const [allowMessages, setAllowMessages] = useState(true);
  const [showReadReceipts, setShowReadReceipts] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  // Social sharing toggles (per-field visibility)
  const [showPreferredName, setShowPreferredName] = useState(true);
  const [showEmail, setShowEmail] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showState, setShowState] = useState(true);
  const [showJob, setShowJob] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [socialOpen, setSocialOpen] = useState(false);

  // Browser-only toggles (localStorage)
  const [messageSound, setMessageSound] = useState(() => {
    return safeGetItem("dupont_message_sound") !== "false";
  });
  const [browserNotifications, setBrowserNotifications] = useState(() => {
    return safeGetItem("dupont_browser_notifications") === "true";
  });

  const [saveMessage, setSaveMessage] = useState(null);

  // Load server values when profile data arrives
  useEffect(() => {
    if (profileData) {
      setAllowMessages(profileData.allow_messages ?? true);
      setShowReadReceipts(profileData.show_read_receipts ?? true);
      setIsVisible(profileData.is_visible ?? true);
      setShowPreferredName(profileData.show_preferred_name ?? true);
      setShowEmail(profileData.show_email ?? true);
      setShowPhone(profileData.show_phone ?? true);
      setShowState(profileData.show_state ?? true);
      setShowJob(profileData.show_job ?? true);
      setShowDescription(profileData.show_description ?? true);
    }
  }, [profileData]);

  // Save browser-only toggles to localStorage immediately
  const handleSoundToggle = () => {
    const next = !messageSound;
    setMessageSound(next);
    safeSetItem("dupont_message_sound", next ? "true" : "false");
  };

  const handleNotificationsToggle = async () => {
    if (!browserNotifications) {
      // Turning ON — ask browser for permission first
      if ("Notification" in window) {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          setBrowserNotifications(true);
          safeSetItem("dupont_browser_notifications", "true");
        }
        // If denied, stay off — user needs to allow in browser settings
      }
    } else {
      // Turning OFF
      setBrowserNotifications(false);
      safeSetItem("dupont_browser_notifications", "false");
    }
  };

  // Save server-side toggles
  const handleSave = async () => {
    // Only save once the saved profile has arrived. Before that, every toggle
    // shows its "on" default, and saving would overwrite the member's choices.
    if (!individualId || !profileLoaded) return;

    // Include all existing profile fields so the upsert doesn't erase them
    const payload = {
      individual_id: individualId,
      profile_picture_url: profileData?.profile_picture_url || null,
      closeup_picture_url: profileData?.closeup_picture_url || null,
      preferred_name: profileData?.preferred_name || null,
      state_of_residency: profileData?.state_of_residency || null,
      email: profileData?.email || null,
      phone_number: profileData?.phone_number || null,
      job_or_passion: profileData?.job_or_passion || null,
      description: profileData?.description || null,
      is_visible: isVisible,
      allow_messages: allowMessages,
      show_read_receipts: showReadReceipts,
      show_preferred_name: showPreferredName,
      show_email: showEmail,
      show_phone: showPhone,
      show_state: showState,
      show_job: showJob,
      show_description: showDescription,
    };

    try {
      await upsertProfile(payload).unwrap();
      setSaveMessage("Settings saved!");
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage("Something went wrong. Please try again.");
      setTimeout(() => setSaveMessage(null), 4000);
    }
  };

  const heading = (
    <h2 className="text-[28px] font-display font-semibold text-dupontBlue dark:text-darkText">
      Correspondence Settings
    </h2>
  );

  // Sound and pop-ups live in this browser, not in the profile, so they show
  // whether or not the profile loaded.
  const deviceSettings = (
    <div className="flex flex-col gap-y-[20px]">
      <h3 className="text-[18px] font-display font-semibold text-dupontBlue dark:text-darkText">
        Notifications
        <span className="text-[13px] font-body text-dupontGray dark:text-darkTextSecondary font-normal ml-[8px]">
          (this device only)
        </span>
      </h3>

      <ToggleRow
        label="Message sound"
        helper="Play a gentle chime when a new letter arrives while you're using the app."
        checked={messageSound}
        onChange={handleSoundToggle}
      />

      <ToggleRow
        label="Browser notifications"
        helper="Show a desktop pop-up when new letters arrive, even if this tab is in the background."
        checked={browserNotifications}
        onChange={handleNotificationsToggle}
      />

      <p className="text-[13px] font-body text-dupontGray dark:text-darkTextSecondary italic">
        Sound and notification preferences are saved automatically for this browser.
      </p>
    </div>
  );

  // The saved settings haven't arrived (null = "no profile yet" is a real
  // answer). The profile toggles would all read "on", whatever the member
  // chose, and could be saved that way, so they wait. That includes a "Try
  // again" in flight, when the query reports neither loading nor error.
  // Without a signed-in member id the query never runs, so that is an error
  // too, not an endless "Loading" (and there is nothing to retry).
  if (!profileLoaded) {
    return (
      <div className="flex flex-col gap-y-[32px] max-w-[600px]">
        {heading}
        {!individualId ? (
          <SharedFeatureError
            title="We couldn't load your settings"
            message="Nothing has been changed. Please reload the page, or sign out and back in."
          />
        ) : isError && !isFetching ? (
          <SharedFeatureError
            title="We couldn't load your settings"
            message="Nothing has been changed. Please try again in a moment."
            onRetry={refetch}
          />
        ) : (
          <SharedLoading />
        )}
        <div className="correspondence-divider" />
        {deviceSettings}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-[32px] max-w-[600px]">
      {heading}

      {/* Messaging section */}
      <div className="flex flex-col gap-y-[20px]">
        <h3 className="text-[18px] font-display font-semibold text-dupontBlue dark:text-darkText">
          Messaging
        </h3>

        <ToggleRow
          label="Allow family members to send me letters"
          helper="When off, no one can start a new conversation with you."
          checked={allowMessages}
          onChange={() => setAllowMessages(!allowMessages)}
        />

        <ToggleRow
          label="Read receipts"
          helper="When on, people you correspond with can see when you've read their letter."
          checked={showReadReceipts}
          onChange={() => setShowReadReceipts(!showReadReceipts)}
        />
      </div>

      <div className="correspondence-divider" />

      {/* Directory section */}
      <div className="flex flex-col gap-y-[20px]">
        <h3 className="text-[18px] font-display font-semibold text-dupontBlue dark:text-darkText">
          Directory
        </h3>

        <ToggleRow
          label="Profile visibility"
          helper="When off, your profile won't appear in the Family Connect directory."
          checked={isVisible}
          onChange={() => setIsVisible(!isVisible)}
        />
      </div>

      <div className="correspondence-divider" />

      {deviceSettings}

      {showSocialSettings && (
        <>
          <div className="correspondence-divider" />

          {/* Social Settings (collapsible dark/gold panel — same as My Profile) */}
          <div className="flex flex-col gap-y-[20px]">
            <h3 className="text-[18px] font-display font-semibold text-dupontBlue dark:text-darkText">
              Social Settings
            </h3>
            <p className="text-[15px] font-body text-dupontBlue/70 dark:text-darkText/70">
              Control which profile fields are visible to your cousins in the Family Connect directory.
            </p>
            <div className="rounded-[12px] overflow-hidden border border-dupontGold/40">
              <button
                type="button"
                aria-expanded={socialOpen}
                className="flex w-full items-center justify-between px-[16px] py-[12px] bg-dupontBlue cursor-pointer select-none text-left"
                onClick={() => setSocialOpen((o) => !o)}
              >
                <span className="text-[17px] font-display font-bold text-dupontGold">
                  Sharing Preferences
                </span>
                <span className="text-dupontGold text-[18px]" aria-hidden="true">
                  {socialOpen ? "\u25B2" : "\u25BC"}
                </span>
              </button>

              {socialOpen && (
                <div className="bg-dupontBlue/95 px-[16px] divide-y divide-dupontCream/10">
                  <SharingToggle
                    label="Preferred Name"
                    helperText="Let cousins see your nickname"
                    checked={showPreferredName}
                    onChange={() => setShowPreferredName(!showPreferredName)}
                  />
                  <SharingToggle
                    label="Email"
                    helperText="Let cousins see your email address"
                    checked={showEmail}
                    onChange={() => setShowEmail(!showEmail)}
                  />
                  <SharingToggle
                    label="Phone"
                    helperText="Let cousins see your phone number"
                    checked={showPhone}
                    onChange={() => setShowPhone(!showPhone)}
                  />
                  <SharingToggle
                    label="State / Region"
                    helperText="Let cousins see where you live"
                    checked={showState}
                    onChange={() => setShowState(!showState)}
                  />
                  <SharingToggle
                    label="Job / Passion"
                    helperText="Let cousins see what you do"
                    checked={showJob}
                    onChange={() => setShowJob(!showJob)}
                  />
                  <SharingToggle
                    label="About You"
                    helperText="Let cousins see your description"
                    checked={showDescription}
                    onChange={() => setShowDescription(!showDescription)}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="correspondence-divider" />

      {/* Save button for server-side toggles */}
      <div className="flex flex-row items-center gap-x-[16px]">
        <SharedButton onClick={handleSave} disabled={isSaving || !profileLoaded}>
          {isSaving ? "Saving..." : "Save Settings"}
        </SharedButton>
        {saveMessage && (
          <span className="text-[14px] font-body text-dupontGold italic">
            {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}

/* Reusable toggle row with label, helper text, and a styled switch */
function ToggleRow({ label, helper, checked, onChange }) {
  return (
    <div className="flex flex-row items-start gap-x-[16px]">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className="mt-[2px] flex-shrink-0 relative inline-flex h-[24px] w-[44px] items-center rounded-full transition-colors duration-200 focus:outline-none"
        style={{
          backgroundColor: checked ? "#b5a03e" : "#d1d5db",
        }}
      >
        <span
          className="inline-block h-[20px] w-[20px] rounded-full bg-white shadow transition-transform duration-200"
          style={{
            transform: checked ? "translateX(22px)" : "translateX(2px)",
          }}
        />
      </button>
      <div className="flex flex-col">
        <span className="text-[17px] font-body text-dupontBlue dark:text-darkText font-semibold">
          {label}
        </span>
        <span className="text-[15px] font-body text-dupontBlue/70 dark:text-darkText/70">
          {helper}
        </span>
      </div>
    </div>
  );
}

/* Toggle used inside the dark Social Settings panel: a real switch button
   inside the label, so it works from the keyboard and a click anywhere on the
   row flips it (same as My Profile's). */
function SharingToggle({ label, helperText, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-[10px] cursor-pointer">
      <div className="flex flex-col gap-y-[2px]">
        <span className="text-[17px] font-display font-bold text-dupontGold">
          {label}
        </span>
        {helperText && (
          <span className="text-[15px] font-body text-dupontCream/90">
            {helperText}
          </span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`relative w-[44px] h-[24px] rounded-full transition-colors ${
          checked ? "bg-dupontGold" : "bg-dupontCream/30"
        }`}
        onClick={onChange}
      >
        <span
          className={`absolute top-[2px] left-0 w-[20px] h-[20px] rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-[2px]"
          }`}
        />
      </button>
    </label>
  );
}

export default CorrespondenceSettings;
